import React, { useEffect, useState } from "react";
import { useAuth } from "../components/AuthContext";

const SEVERITY_CONFIG = {
  critical: { bg: '#dc2626', color: 'white', icon: '🚨', label: 'Critical' },
  high: { bg: '#f59e0b', color: 'white', icon: '⚠️', label: 'High' },
  medium: { bg: '#3b82f6', color: 'white', icon: '📋', label: 'Medium' },
  low: { bg: '#6b7280', color: 'white', icon: 'ℹ️', label: 'Low' }
};

const CATEGORY_ICONS = {
  pickup_delay: '🚚',
  delivery_delay: '📦',
  carrier_unassigned: '❓',
  document_missing: '📄',
  rate_anomaly: '💰',
  customer_churn: '👤',
  carrier_performance: '📊',
  capacity_warning: '📈'
};

export default function AlertsDashboard() {
  const { adminEmail } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPrescriptions, setLoadingPrescriptions] = useState(false);
  const [filter, setFilter] = useState('all');

  const headers = { 'x-admin-email': adminEmail, 'x-user-role': 'BROKER_ADMIN' };

  useEffect(() => { loadAlerts(); }, []);

  async function loadAlerts() {
    setLoading(true);
    try {
      const [alertsRes, summaryRes] = await Promise.all([
        fetch('/api/alerts', { headers }).then(r => r.json()),
        fetch('/api/alerts/summary', { headers }).then(r => r.json())
      ]);
      setAlerts(alertsRes.alerts || []);
      setSummary(summaryRes);
    } catch (e) {
      console.error('Load alerts error:', e);
    } finally {
      setLoading(false);
    }
  }

  async function loadPrescriptions() {
    setLoadingPrescriptions(true);
    try {
      const res = await fetch('/api/alerts/prescriptions', { headers });
      const data = await res.json();
      setPrescriptions(data.prescriptions || []);
    } catch (e) {
      console.error('Prescriptions error:', e);
    } finally {
      setLoadingPrescriptions(false);
    }
  }

  const filteredAlerts = filter === 'all' 
    ? alerts 
    : alerts.filter(a => a.severity === filter);

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center' }}>Loading alerts...</div>;
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 900 }}>🚨 Disruption Alerts</h1>
          <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: 14 }}>
            Proactive monitoring & AI-powered prescriptions
          </p>
        </div>
        <button 
          onClick={loadAlerts}
          style={{ padding: '8px 16px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}
        >
          🔄 Refresh
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12, marginBottom: 24 }}>
          <div 
            onClick={() => setFilter('all')}
            style={{ 
              background: filter === 'all' ? '#111827' : 'white', 
              color: filter === 'all' ? 'white' : '#111827',
              border: '1px solid #e5e7eb', 
              borderRadius: 12, 
              padding: 16, 
              textAlign: 'center',
              cursor: 'pointer'
            }}
          >
            <div style={{ fontSize: 28, fontWeight: 900 }}>{summary.total}</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>Total</div>
          </div>
          {Object.entries(summary.bySeverity || {}).map(([sev, count]) => {
            const config = SEVERITY_CONFIG[sev];
            if (!config) return null;
            return (
              <div 
                key={sev}
                onClick={() => setFilter(sev)}
                style={{ 
                  background: filter === sev ? config.bg : 'white', 
                  color: filter === sev ? 'white' : '#111827',
                  border: `2px solid ${config.bg}`, 
                  borderRadius: 12, 
                  padding: 16, 
                  textAlign: 'center',
                  cursor: 'pointer'
                }}
              >
                <div style={{ fontSize: 28, fontWeight: 900 }}>{count}</div>
                <div style={{ fontSize: 12 }}>{config.icon} {config.label}</div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
        {/* Alerts List */}
        <div>
          <h2 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 700 }}>
            Active Alerts ({filteredAlerts.length})
          </h2>
          
          {filteredAlerts.length === 0 ? (
            <div style={{ background: '#d1fae5', border: '1px solid #a7f3d0', borderRadius: 12, padding: 24, textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
              <div style={{ fontWeight: 700, color: '#065f46' }}>All Clear!</div>
              <div style={{ color: '#047857', fontSize: 14 }}>No active alerts at this time</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              {filteredAlerts.map(alert => {
                const sevConfig = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.medium;
                const catIcon = CATEGORY_ICONS[alert.category] || '📋';
                return (
                  <div 
                    key={alert.id}
                    style={{ 
                      background: 'white', 
                      border: '1px solid #e5e7eb',
                      borderLeft: `4px solid ${sevConfig.bg}`,
                      borderRadius: 12, 
                      padding: 16 
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 20 }}>{catIcon}</span>
                        <span style={{ fontWeight: 700 }}>{alert.title}</span>
                      </div>
                      <span style={{ 
                        padding: '4px 8px', 
                        borderRadius: 4, 
                        fontSize: 11, 
                        fontWeight: 600,
                        background: sevConfig.bg,
                        color: sevConfig.color
                      }}>
                        {sevConfig.icon} {sevConfig.label}
                      </span>
                    </div>
                    <div style={{ color: '#4b5563', fontSize: 14, marginBottom: 12 }}>
                      {alert.message}
                    </div>
                    {alert.actions && alert.actions.length > 0 && (
                      <div style={{ display: 'flex', gap: 8 }}>
                        {alert.actions.map((action, i) => (
                          <button
                            key={i}
                            style={{ 
                              padding: '6px 12px', 
                              background: i === 0 ? '#dc2626' : '#f3f4f6',
                              color: i === 0 ? 'white' : '#374151',
                              border: 'none', 
                              borderRadius: 6, 
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                          >
                            {action.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Prescriptions Panel */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>💡 AI Prescriptions</h2>
            <button 
              onClick={loadPrescriptions}
              disabled={loadingPrescriptions}
              style={{ 
                padding: '6px 12px', 
                background: loadingPrescriptions ? '#9ca3af' : '#8b5cf6', 
                color: 'white', 
                border: 'none', 
                borderRadius: 6, 
                fontSize: 12,
                fontWeight: 600,
                cursor: loadingPrescriptions ? 'not-allowed' : 'pointer'
              }}
            >
              {loadingPrescriptions ? '...' : '🧠 Generate'}
            </button>
          </div>

          {prescriptions.length === 0 ? (
            <div style={{ background: '#f3f4f6', borderRadius: 12, padding: 24, textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🧠</div>
              <div style={{ color: '#6b7280', fontSize: 14 }}>
                Click "Generate" to get AI-powered action recommendations
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              {prescriptions.map((rx, i) => {
                const priorityColors = {
                  immediate: { bg: '#fee2e2', border: '#fecaca', color: '#dc2626' },
                  today: { bg: '#fef3c7', border: '#fde68a', color: '#d97706' },
                  this_week: { bg: '#dbeafe', border: '#bfdbfe', color: '#2563eb' }
                };
                const pc = priorityColors[rx.priority] || priorityColors.today;
                return (
                  <div 
                    key={i}
                    style={{ 
                      background: pc.bg, 
                      border: `1px solid ${pc.border}`,
                      borderRadius: 12, 
                      padding: 16 
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ 
                        padding: '2px 8px', 
                        borderRadius: 4, 
                        fontSize: 10, 
                        fontWeight: 700,
                        background: pc.color,
                        color: 'white',
                        textTransform: 'uppercase'
                      }}>
                        {(rx.priority || 'today').replace('_', ' ')}
                      </span>
                      <span style={{ fontSize: 11, color: '#6b7280' }}>
                        Effort: {rx.effort || 'medium'}
                      </span>
                    </div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{rx.action}</div>
                    <div style={{ fontSize: 13, color: '#4b5563' }}>
                      💎 {rx.impact}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
